/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, jsx } from '@atlaskit/css';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { captureException } from '@atlassian/error-handling';

import type { SOURCE_COMPONENT, SOURCE_CONTEXT } from '../../constants';
import { normalizeSeat } from '../../services/user-access';

import { ModalProvider, useModalContext, useModalState } from './contexts/modal-context';
import { ConfirmationScreen } from './screens/confirmation-screen';
import { messages as confirmationMessages } from './screens/confirmation-screen/messages';
import { ErrorScreen } from './screens/error-screen';
import { LoadingScreen } from './screens/loading-screen';
import { NormalizeSeatScreen } from './screens/normalize-seat-screen';
import { ProvisioningScreen } from './screens/provisioning-screen';
import { RequestedTrialScreen } from './screens/requested-trial-screen';
import { TimeoutScreen } from './screens/timeout-screen';
import { WelcomeScreen } from './screens/welcome-screen';
import { useConfirmationScreenMessages } from './useConfirmationScreenMessages';
import { useMessages } from './utils/i18n';
import { hasRequestedTrial, saveRequestedTrialToLocalStorage } from './utils/local-storage';
import { sendRequestTrial } from './utils/send-request-trial';

const styles = cssMap({
	modalWrapper: {
		position: 'relative',
		width: '100%',
		overflow: 'hidden',
		maxWidth: '968px',
		borderRadius: token('radius.small'),
		minHeight: '568px',
	},
});

type Props = {
	cloudId: string;
	locale?: string;
	onClose: () => void;
	onComplete?: () => void;
	onTryNowClick?: () => void;
	orgId?: string;
	sourceComponent: (typeof SOURCE_COMPONENT)[keyof typeof SOURCE_COMPONENT];
	sourceContext: (typeof SOURCE_CONTEXT)[keyof typeof SOURCE_CONTEXT];
	testId?: string;
};

type ModalProps = Pick<Props, 'onClose' | 'onComplete' | 'onTryNowClick' | 'testId'>;

const TwcXflowModal = ({ onClose, onComplete, onTryNowClick, testId }: ModalProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const {
		cloudId,
		sourceComponent,
		sourceContext,
		currentScreen,
		setCurrentScreen,
		modalState,
		redirectUrl,
		setRedirectUrl,
		isNormalizeSeatsEnabled,
	} = useModalContext();

	const [approvalUrl, setApprovalUrl] = useState<string | undefined>(undefined);

	const canActivate = useMemo(
		() => modalState.activationCapability === 'can-activate',
		[modalState.activationCapability],
	);

	useEffect(() => {
		if (!cloudId) {
			setCurrentScreen('error');
		}
	}, [cloudId, setCurrentScreen]);

	const handleTryNowClick = useCallback(() => {
		onTryNowClick?.();
		if (canActivate) {
			setCurrentScreen('provisioning');
			return;
		} else {
			const [hasRequested, requestedTrialData] = hasRequestedTrial(cloudId);
			if (hasRequested) {
				setApprovalUrl(requestedTrialData.link);
				setCurrentScreen('requested-trial-screen');
				return;
			} else {
				sendRequestTrial(
					cloudId,
					modalState.edition,
					formatMessage(confirmationMessages.requestTrialComment),
				)
					.then((response) => {
						if (response?.approvalUrl) {
							saveRequestedTrialToLocalStorage(cloudId, response.approvalUrl);
							setApprovalUrl(response.approvalUrl);
						}
						setCurrentScreen('requested-trial-screen');
					})
					.catch(() => {
						setCurrentScreen('error');
					});
			}
		}
	}, [setCurrentScreen, onTryNowClick, modalState.edition, cloudId, canActivate, formatMessage]);

	const handleProvisioningComplete = useCallback(
		(redirectUrl: string | undefined) => {
			redirectUrl && setRedirectUrl(redirectUrl);
			setCurrentScreen('welcome');
		},
		[setCurrentScreen, setRedirectUrl],
	);

	const handleWelcomeComplete = useCallback(() => {
		if (fg('twc_normalize_seat_modal_gate_v2')) {
			setCurrentScreen('normalize-seat');
		} else {
			onComplete?.();
			window.location.href = redirectUrl;
		}
	}, [setCurrentScreen, redirectUrl, onComplete]);

	const handleOnClose = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalClose',
			attributes: {
				currentScreen,
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');

		onClose();
	}, [currentScreen, onClose, createAnalyticsEvent, sourceComponent, sourceContext]);

	const handleNormalizeComplete = useCallback(async () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalNormalizeSeatConfirm',
			source: 'twcXflowModalNormalizeSeatScreen',
			attributes: {
				isNormalizeSeatsEnabled,
				edition: modalState.edition,
				hasPaymentDetails: modalState.hasPaymentDetails,
				userProducts: modalState.userProducts,
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');

		if (isNormalizeSeatsEnabled) {
			try {
				await normalizeSeat({ cloudId });
				createAnalyticsEvent({
					eventType: 'track',
					action: 'normalizeSeatStarted',
					actionSubject: 'normalizeSeatJob',
					actionSubjectId: 'twcXflowModal',
					attributes: {
						sourceComponent,
						sourceContext,
					},
				}).fire('growth');
			} catch {
				createAnalyticsEvent({
					eventType: 'track',
					action: 'normalizeSeatStartFailed',
					actionSubject: 'normalizeSeatJob',
					actionSubjectId: 'twcXflowModal',
					attributes: {
						sourceComponent,
						sourceContext,
					},
				}).fire('growth');
			}
		}
		onComplete?.();
		window.location.href = redirectUrl;
	}, [
		isNormalizeSeatsEnabled,
		cloudId,
		redirectUrl,
		createAnalyticsEvent,
		modalState,
		onComplete,
		sourceComponent,
		sourceContext,
	]);

	const { subheader: confirmationSubheaderV2, ctaText: confirmationCtaTextV2 } =
		useConfirmationScreenMessages({
			edition: modalState.edition,
			activationCapability: modalState.activationCapability,
			hasPaymentDetails: modalState.hasPaymentDetails,
		});

	const renderScreen = () => {
		switch (currentScreen) {
			case 'loading':
				return <LoadingScreen setApprovalUrl={setApprovalUrl} />;
			case 'error':
				return <ErrorScreen onClose={handleOnClose} />;
			case 'confirmation':
				return (
					<ConfirmationScreen
						onTryNowClick={handleTryNowClick}
						onClose={handleOnClose}
						canActivate={canActivate}
						edition={modalState.edition}
						hasPaymentDetails={modalState.hasPaymentDetails}
						confirmationSubheader={confirmationSubheaderV2}
						confirmationCtaText={confirmationCtaTextV2}
					/>
				);
			case 'provisioning':
				return <ProvisioningScreen onProvisioningComplete={handleProvisioningComplete} />;
			case 'welcome':
				return <WelcomeScreen onComplete={handleWelcomeComplete} />;
			case 'normalize-seat':
				return <NormalizeSeatScreen onConfirm={handleNormalizeComplete} testId={testId} />;
			case 'requested-trial-screen':
				return <RequestedTrialScreen onClose={handleOnClose} approvalUrl={approvalUrl} />;
			case 'timeout':
				return <TimeoutScreen onClose={handleOnClose} />;
		}
	};

	const shouldAllowClose =
		currentScreen === 'error' ||
		currentScreen === 'confirmation' ||
		currentScreen === 'loading' ||
		currentScreen === 'requested-trial-screen' ||
		currentScreen === 'timeout';

	return (
		<ModalTransition>
			<Modal
				shouldCloseOnOverlayClick={shouldAllowClose}
				shouldCloseOnEscapePress={shouldAllowClose}
				onClose={handleOnClose}
				testId={testId}
				width="x-large"
			>
				<Box xcss={styles.modalWrapper}>
					<ErrorBoundary
						FallbackComponent={() => <ErrorScreen onClose={handleOnClose} />}
						onError={(error: Error) => {
							captureException('Unhandled error in TWC XFlow modal', error, {
								cloudId,
								packageName: '@atlassian/twc-xflow-modal',
								teamName: 'starburst',
							});
						}}
					>
						{renderScreen()}
					</ErrorBoundary>
				</Box>
			</Modal>
		</ModalTransition>
	);
};

const TwcXflowModalWithoutMessages = (props: Props) => {
	const {
		cloudId,
		sourceComponent,
		sourceContext,
		onClose,
		onComplete,
		onTryNowClick,
		testId,
		orgId,
	} = props;

	const contextValue = useModalState(cloudId, sourceComponent, sourceContext, 'en', orgId);

	return (
		<ModalProvider value={contextValue}>
			<IntlProvider locale={'en'}>
				<TwcXflowModal
					onClose={onClose}
					onComplete={onComplete}
					onTryNowClick={onTryNowClick}
					testId={testId}
				/>
			</IntlProvider>
		</ModalProvider>
	);
};

const TwcXflowModalWithMessages = (props: Props) => {
	const {
		locale: requestedLocale = 'en',
		cloudId,
		sourceComponent,
		sourceContext,
		onClose,
		onComplete,
		onTryNowClick,
		testId,
		orgId,
	} = props;
	const { isLoading, locale, messages } = useMessages(requestedLocale);

	const contextValue = useModalState(cloudId, sourceComponent, sourceContext, locale, orgId);

	if (isLoading) {
		return null;
	}

	return (
		<ModalProvider value={contextValue}>
			<IntlProvider locale={locale} messages={messages}>
				<TwcXflowModal
					onClose={onClose}
					onComplete={onComplete}
					onTryNowClick={onTryNowClick}
					testId={testId}
				/>
			</IntlProvider>
		</ModalProvider>
	);
};

export default componentWithFG(
	'grw-twc-skip-locale-check',
	TwcXflowModalWithMessages,
	TwcXflowModalWithoutMessages,
);
